import { useController } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { useBoolean } from "@miplatform/hooks";
import { mdiClose } from "@mdi/js";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { MiChip } from "../MiChip/MiChip";
import { MiIcon } from "../MiIcon/MiIcon";

// RHF: remove this file after migration
// TODO: Update MiInputSelectBox to be able to extends it from MiInputMultiSelectBox
function SelectInput({
  labelText,
  menuItems = [],
  value,
  error,
  handleChange,
  disabled,
  disableNone,
  required,
  ...rest
}) {
  return (
    <FormControl fullWidth error={!!error} required={required} size="small">
      <InputLabel id="select-label" shrink>
        {labelText}
      </InputLabel>
      <Select
        labelId="select-label"
        disabled={disabled}
        label={labelText}
        value={value}
        onChange={handleChange}
        inputProps={{ "data-testid": `mi-selectbox-${labelText}` }}
        notched
        {...rest}
      >
        {!disableNone && (
          <MenuItem value="None" data-testid="disable-none">
            None
          </MenuItem>
        )}
        {menuItems?.length > 0 &&
          menuItems.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
      </Select>
      <FormHelperText>{error ? error.message : ""}</FormHelperText>
    </FormControl>
  );
}

export function MiControlledInputMultiSelectBox({
  multiple = true,
  value = [],
  readOnly,
  required,
  menuItems = [],
  handleChange,
  name,
  labelText,
  control,
  rules,
  style,
  ...rest
}) {
  const [isOpen, handleOpen, handleClose] = useBoolean();
  const useStyles = makeStyles()((theme) => ({
    chiplabel: {
      borderRadius: 3,
      height: 20,
      fontWeight: theme.fontWeight.medium,
      fontSize: theme.fontSize.xsmall,
      color: theme.palette.bluegrey.dark,
      backgroundColor: theme.palette.primary.light,
    },
    icon: {
      color: theme.palette.grey.main,
      cursor: "pointer",
    },
    selectIcon: {
      width: 24,
      height: 24,
      cursor: "pointer",
    },
  }));

  const {
    field: { onChange, value: defaultValue = [], onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const { classes } = useStyles();
  const handleClearClick = (e) => {
    e.stopPropagation();
    onChange([]);
    if (handleChange) {
      handleChange({ target: { value: [] } });
    }
  };

  const getSelectedLabels = (selectedValues) => {
    const selectedLabels = [];
    if (menuItems?.length) {
      menuItems?.forEach((item) => {
        // If the item is a group, then we need to iterate over the menuItems of the group
        if (item.type === "group") {
          item.menuItems.forEach((subItem) => {
            if (selectedValues.includes(subItem.value)) {
              selectedLabels.push(subItem);
            }
          });
        } else if (selectedValues.includes(item.value)) {
          selectedLabels.push(item);
        }
      });
    }
    return selectedLabels;
  };

  const getSelectedChips = () => {
    const arr = getSelectedLabels(defaultValue);
    return (
      <div
        style={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          ...rest?.chipsStyle,
        }}
      >
        {arr.map((option) => (
          <div key={option.id || option.value}>
            <MiChip label={option.label} className={classes.chiplabel} />
            {!!option.secondaryInfo && (
              <div>
                <i>Pay Date: </i>
                {option.secondaryInfo}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <SelectInput
      {...(!readOnly &&
        !rest.disabled && {
          open: isOpen,
          onOpen: handleOpen,
          onClose: handleClose,
        })}
      menuItems={menuItems}
      renderValue={getSelectedChips}
      multiple={multiple}
      value={value?.length ? value : defaultValue || []}
      handleChange={handleChange || onChange}
      fullWidth
      labelText={labelText}
      readOnly={readOnly}
      required={required}
      onBlur={onBlur}
      endAdornment={
        <>
          {defaultValue.length > 0 && !readOnly && !rest.disabled && (
            <MiIcon
              onClick={handleClearClick}
              path={mdiClose}
              style={{ ...rest.endAdornmentStyle }}
              className={classes.icon}
            />
          )}
          {!readOnly && !rest.disabled && (
            <Box className={classes.selectIcon} onClick={handleOpen} />
          )}
        </>
      }
      {...rest}
      error={!!error && !readOnly ? error : undefined}
      data-testid={`mi-multiselectbox-${labelText}`}
    />
  );
}
